import axios from 'axios';

const request = () => window.axios || axios;

export const DEFAULT_SUFFICIT_API_URL = 'https://endpoints.sufficit.com.br';
export const DEFAULT_SUFFICIT_IDENTITY_URL = 'https://identity.sufficit.com.br';
export const DEFAULT_SUFFICIT_OAUTH_CLIENT_ID = 'hermes';
export const DEFAULT_SUFFICIT_PORTAL_URL = DEFAULT_SUFFICIT_IDENTITY_URL;
export const DEFAULT_SUFFICIT_CONTEXTS_ENDPOINT = '/gateway/hermes/contexts';
export const DEFAULT_SUFFICIT_EXTENSIONS_ENDPOINT_TEMPLATE =
  '/gateway/hermes/contexts/%{context_id}/extensions';
export const DEFAULT_SUFFICIT_FALLBACK_ENDPOINTS_PATH =
  '/Telephony/ChromeExtension/EndPoints';

const buildBasePath = accountId =>
  `/api/v1/accounts/${accountId}/integrations/sufficit`;

const ensureTrailingSlash = value => {
  return value.endsWith('/') ? value : `${value}/`;
};

const buildRemoteUrl = (baseUrl, endpoint) => {
  if (!baseUrl) {
    throw new Error('Sufficit API URL is required.');
  }

  if (/^https?:\/\//i.test(endpoint || '')) {
    return endpoint;
  }

  return new URL(
    String(endpoint || '').replace(/^\//, ''),
    ensureTrailingSlash(baseUrl)
  ).toString();
};

const extractPayloadCollection = payload => {
  return (
    payload?.contexts ||
    payload?.extensions ||
    payload?.items ||
    payload?.data ||
    payload
  );
};

const normalizeContext = context => {
  if (!context || typeof context !== 'object') {
    return null;
  }

  const contextId =
    context.id || context.context_id || context.uuid || context.key;
  const transport = String(
    context.sip_transport ||
      context.transport ||
      context?.sip?.transport ||
      'wss'
  ).toLowerCase();
  const sipServer =
    context.sip_server ||
    context.server ||
    context.host ||
    context?.sip?.server ||
    context?.preferences?.sip_server;

  if (!contextId || !sipServer) {
    return null;
  }

  return {
    id: String(contextId),
    name:
      context.name ||
      context.label ||
      context.description ||
      `Context ${contextId}`,
    sip_server: String(sipServer),
    sip_port: Number(
      context.sip_port ||
        context.port ||
        context?.sip?.port ||
        (transport === 'ws' ? 8088 : 8089)
    ),
    sip_transport: transport,
    sip_realm:
      context.sip_realm ||
      context.realm ||
      context.domain ||
      context?.sip?.realm ||
      sipServer,
    registration_mode:
      context.registration_mode ||
      context?.sip?.registration_mode ||
      'per_agent',
    inbox_name: context.inbox_name || context.name || '',
    raw: context,
  };
};

const normalizeExtensionOption = option => {
  if (!option || typeof option !== 'object') {
    return null;
  }

  const extension =
    option.extension ||
    option.number ||
    option.username ||
    option.ramal ||
    option.id;

  if (!extension) {
    return null;
  }

  const displayName =
    option.display_name ||
    option.name ||
    option.label ||
    option.description ||
    `Extension ${extension}`;

  return {
    value: String(extension),
    label: `${displayName} (${extension})`,
    extension: String(extension),
    password: option.password || option.secret || option.token || '',
    display_name: displayName,
    raw: option,
  };
};

const parseRemoteResponse = async (response, requestUrl) => {
  const bodyText = await response.text();
  let parsedBody = {};

  if (bodyText) {
    try {
      parsedBody = JSON.parse(bodyText);
    } catch {
      throw new Error('Sufficit returned an invalid JSON response.');
    }
  }

  if (response.ok) {
    return parsedBody;
  }

  const remoteMessage =
    parsedBody?.message ||
    parsedBody?.error ||
    bodyText ||
    `Sufficit request failed (${response.status}).`;

  const error = new Error(remoteMessage);
  error.status = response.status;
  error.requestUrl = requestUrl;
  throw error;
};

const fetchRemoteJson = async (url, options = {}) => {
  const response = await fetch(url, options);
  return parseRemoteResponse(response, url);
};

const buildLegacyContextFromEndpoint = endpoint => {
  if (!endpoint || typeof endpoint !== 'object') {
    return null;
  }

  const contextId =
    endpoint.contextId || endpoint.context_id || endpoint.context;
  if (!contextId) {
    return null;
  }

  const contextTitle =
    endpoint.contextTitle || endpoint.context_title || `Context ${contextId}`;

  return {
    id: String(contextId),
    name: contextTitle,
    sip_server: 'voip.sufficit.com.br',
    sip_port: 8089,
    sip_transport: 'wss',
    sip_realm: 'voip.sufficit.com.br',
    registration_mode: 'per_agent',
    inbox_name: contextTitle,
    raw: endpoint,
  };
};

const buildLegacyExtensionOption = endpoint => {
  if (!endpoint || typeof endpoint !== 'object') {
    return null;
  }

  const endpointId = endpoint.id || endpoint.endpointId || endpoint.endpoint_id;
  if (!endpointId) {
    return null;
  }

  const displayName =
    endpoint.description ||
    endpoint.title ||
    endpoint.name ||
    `Extension ${endpointId}`;

  const extension = `${String(endpointId).replace(/-/g, '')}-wss`;

  return {
    value: extension,
    label: `${displayName} (${extension})`,
    extension,
    password: endpoint.password || '',
    display_name: displayName,
    raw: endpoint,
  };
};

export const fetchSufficitState = accountId =>
  request().get(buildBasePath(accountId));

export const authorizeSufficit = (accountId, payload) =>
  request().post(`${buildBasePath(accountId)}/authorize`, payload);

export const connectSufficit = (accountId, payload) =>
  request().post(`${buildBasePath(accountId)}/connect`, payload);

export const refreshSufficitContexts = (accountId, payload = {}) =>
  request().post(`${buildBasePath(accountId)}/refresh_contexts`, payload);

export const disconnectSufficit = accountId =>
  request().delete(`${buildBasePath(accountId)}/disconnect`);

export const createSufficitInbox = (accountId, payload) =>
  request().post(`${buildBasePath(accountId)}/inboxes`, payload);

export const fetchSufficitExtensionOptions = (accountId, inboxId, userId) =>
  request().get(
    `${buildBasePath(accountId)}/inboxes/${inboxId}/extension_options`,
    {
      params: {
        user_id: userId,
      },
    }
  );

export const fetchSufficitBrowserContexts = async ({
  siteUrl,
  contextsEndpoint = DEFAULT_SUFFICIT_CONTEXTS_ENDPOINT,
}) => {
  const url = buildRemoteUrl(siteUrl, contextsEndpoint);
  const options = {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  };

  try {
    const payload = await fetchRemoteJson(url, options);
    return (
      Array.isArray(extractPayloadCollection(payload))
        ? extractPayloadCollection(payload)
        : []
    )
      .map(normalizeContext)
      .filter(Boolean);
  } catch (error) {
    if (
      error?.status !== 404 ||
      !/^\/(gateway|api\/v1)\/hermes\//.test(String(contextsEndpoint))
    ) {
      throw error;
    }

    const fallbackUrl = buildRemoteUrl(
      siteUrl,
      DEFAULT_SUFFICIT_FALLBACK_ENDPOINTS_PATH
    );
    const payload = await fetchRemoteJson(fallbackUrl, options);
    const items = Array.isArray(payload)
      ? payload
      : extractPayloadCollection(payload);

    return Array.from(
      new Map(
        (Array.isArray(items) ? items : [])
          .map(buildLegacyContextFromEndpoint)
          .filter(Boolean)
          .map(item => [item.id, item])
      ).values()
    );
  }
};

export const fetchSufficitBrowserExtensionOptions = async ({
  siteUrl,
  contextId,
  extensionsEndpointTemplate = DEFAULT_SUFFICIT_EXTENSIONS_ENDPOINT_TEMPLATE,
  agent,
}) => {
  const endpoint = String(extensionsEndpointTemplate).replace(
    '%{context_id}',
    encodeURIComponent(String(contextId || ''))
  );
  const url = new URL(buildRemoteUrl(siteUrl, endpoint));

  if (agent?.id) {
    url.searchParams.set('user_id', String(agent.id));
  }
  if (agent?.email) {
    url.searchParams.set('user_email', agent.email);
  }
  if (agent?.name) {
    url.searchParams.set('user_name', agent.name);
  }

  const options = {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  };

  try {
    const payload = await fetchRemoteJson(url.toString(), options);
    return (
      Array.isArray(extractPayloadCollection(payload))
        ? extractPayloadCollection(payload)
        : []
    )
      .map(normalizeExtensionOption)
      .filter(Boolean);
  } catch (error) {
    if (
      error?.status !== 404 ||
      !/^\/(gateway|api\/v1)\/hermes\//.test(
        String(extensionsEndpointTemplate)
      )
    ) {
      throw error;
    }

    const fallbackUrl = new URL(
      buildRemoteUrl(siteUrl, DEFAULT_SUFFICIT_FALLBACK_ENDPOINTS_PATH)
    );
    fallbackUrl.searchParams.set('contextId', String(contextId || ''));

    const payload = await fetchRemoteJson(fallbackUrl.toString(), options);
    const items = Array.isArray(payload)
      ? payload
      : extractPayloadCollection(payload);

    return (Array.isArray(items) ? items : [])
      .map(buildLegacyExtensionOption)
      .filter(Boolean);
  }
};
